import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { DEBUG_MODE } from '../config';
import api from '../utils/api';

function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  
  const { register, currentUser, authError, clearAuthError } = useAuth();
  const navigate = useNavigate();
  
  // Clear auth errors when component mounts
  useEffect(() => {
    clearAuthError?.();
  }, [clearAuthError]);
  
  // Set local error state when auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);
  
  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);
  
  // Fetch plans from backend
  useEffect(() => {
    api.get('/plans')
      .then(res => setPlans(res.data.plans || []))
      .catch(() => setPlansError('Failed to load plans'))
      .finally(() => setPlansLoading(false));
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset states
    setError('');
    setSuccess('');
    
    // Form validation
    if (!name || !email || !password || !confirmPassword) {
      return setError('All fields are required');
    }
    
    if (!planId) {
      return setError('Please select a plan');
    }
    
    // Validate password
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    if (password.length < 6) {
      return setError('Password must be at least 6 characters long');
    }
    
    try {
      setIsLoading(true);
      
      if (DEBUG_MODE) console.log('Submitting registration form');
      
      const result = await register(name, email, password, planId);
      
      if (DEBUG_MODE) console.log('Registration result:', result);
      
      if (result.success) {
        setSuccess(result.message || 'Registration successful!');
        setName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setPlanId('');
        // Handle backend redirect
        if (result.redirect === 'dashboard') {
          setTimeout(() => navigate('/dashboard'), 1000);
        } else if (result.redirect === 'payment') {
          setTimeout(() => navigate('/payment', { state: { planId } }), 1000);
        } else {
          // fallback: go to login
          setTimeout(() => navigate('/login', { 
            state: { message: 'Registration successful! You can now log in.' } 
          }), 2000);
        }
      } else {
        setError(result.message || 'Failed to register. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to register. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Container>
      <Row className="justify-content-center mt-5">
        <Col md={6} lg={5}>
          <Card className="shadow-lg border-0">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <h2 className="fw-bold">Create an Account</h2>
                <p className="text-muted">Sign up to get started with AI Integration</p>
              </div>
              
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              <Form onSubmit={handleSubmit}>
                {/* Plan selection */}
                <Form.Group className="mb-3">
                  <Form.Label>Choose a Plan</Form.Label>
                  {plansLoading ? (
                    <div>Loading plans...</div>
                  ) : plansError ? (
                    <div className="text-danger">{plansError}</div>
                  ) : (
                    <Form.Select value={planId} onChange={e => setPlanId(e.target.value)} required>
                      <option value="">Select a plan</option>
                      {plans.map(plan => (
                        <option value={plan.id} key={plan.id}>
                          {plan.name} (${plan.price} / {plan.duration} days)
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoFocus
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Must be at least 6 characters long
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Form.Group>
                
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-100 py-2"
                  disabled={isLoading}
                >
                  {isLoading ? 'Registering...' : 'Register Account'}
                </Button>
              </Form>
              
              <div className="text-center mt-4">
                <p>
                  Already have an account? <Link to="/login">Sign in</Link>
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Register;
